/**
 * Progress bar component for WooCommerce sync operations
 * Auto-hides after 5 seconds when sync is complete
 * Shows detailed sync information
 */

import { useEffect, useState } from 'react';
import { SyncProgress } from '../hooks/useOrders';
import '../styles/SyncProgressBar.css';

interface SyncDetails {
  synced: number;
  updated: number;
  skipped: number;
  errors: number;
}

interface SyncProgressBarProps {
  progress: SyncProgress;
  onClose?: () => void;
  syncDetails?: SyncDetails | null;
}

export function SyncProgressBar({ progress, onClose, syncDetails }: SyncProgressBarProps) {
  const [isHiding, setIsHiding] = useState(false);

  // Auto-hide after 5 seconds when done
  useEffect(() => {
    if (progress.status === 'done' && onClose) {
      const hideTimer = setTimeout(() => {
        setIsHiding(true);
        // Wait for animation to complete before calling onClose
        setTimeout(() => {
          onClose();
          setIsHiding(false);
        }, 500);
      }, 5000);

      return () => clearTimeout(hideTimer);
    }
  }, [progress.status, onClose]);

  if (progress.status === 'idle') {
    return null;
  }

  const percentage = progress.total > 0 
    ? Math.round((progress.current / progress.total) * 100) 
    : 0;

  return (
    <div className={`sync-progress-bar ${progress.status} ${isHiding ? 'hiding' : ''}`}>
      <div className="sync-progress-content">
        <div className="sync-progress-header">
          <span className="sync-progress-icon">
            {progress.status === 'syncing' && '🔄'}
            {progress.status === 'done' && '✅'}
            {progress.status === 'error' && '❌'}
          </span>
          <span className="sync-progress-title">
            {progress.status === 'syncing' && 'Synchronizacja z WooCommerce'}
            {progress.status === 'done' && 'Synchronizacja zakończona'}
            {progress.status === 'error' && 'Błąd synchronizacji'}
          </span>
          {(progress.status === 'done' || progress.status === 'error') && onClose && (
            <button className="sync-progress-close" onClick={() => {
              setIsHiding(true);
              setTimeout(() => {
                onClose();
                setIsHiding(false);
              }, 500);
            }}>×</button>
          )}
        </div>
        
        <div className="sync-progress-track">
          <div 
            className="sync-progress-fill" 
            style={{ width: `${percentage}%` }}
          />
        </div>
        
        <div className="sync-progress-info">
          <span className="sync-progress-count">
            {progress.current} / {progress.total}
          </span>
          <span className="sync-progress-percentage">{percentage}%</span>
        </div>
        
        {progress.message && (
          <div className="sync-progress-message">{progress.message}</div>
        )}

        {/* Detailed sync results when done */}
        {progress.status === 'done' && syncDetails && (
          <div className="sync-details">
            <div className="sync-detail-row">
              <span className="sync-detail-label">📊 Sprawdzono:</span>
              <span className="sync-detail-value">{syncDetails.synced} zamówień</span>
            </div>
            {syncDetails.updated > 0 && (
              <div className="sync-detail-row updated">
                <span className="sync-detail-label">✏️ Zaktualizowano:</span>
                <span className="sync-detail-value">{syncDetails.updated}</span>
              </div>
            )}
            {syncDetails.skipped > 0 && (
              <div className="sync-detail-row skipped">
                <span className="sync-detail-label">⏭️ Pominięto:</span>
                <span className="sync-detail-value">{syncDetails.skipped}</span>
              </div>
            )}
            {syncDetails.errors > 0 && (
              <div className="sync-detail-row errors">
                <span className="sync-detail-label">❌ Błędy:</span>
                <span className="sync-detail-value">{syncDetails.errors}</span>
              </div>
            )}
            <div className="sync-detail-note">
              ℹ️ Dane są aktualizowane bez nadpisywania - tylko brakujące pola
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
